import { Point, Rect } from "../../drawing";
import { NabcSignificantLetter } from "../../gregorio-chant/structures/NabcSignificantLetter";
import { NabcSnippet } from "../../gregorio-chant/structures/NabcSnippet";
import { ChantContext } from "../../gregorio-chant/ChantContext";
import { Glyph } from "../glyphs/Glyph";
import { ChantLayoutElement } from "../layout/ChantLayoutElement";
import { calculateNabcPositionFromPitch } from "../utils";
import { GlyphVisualizer } from "../visualizers";
import { NabcNeume } from "../../gregorio-chant/structures/NabcNeume";
import { Space } from "../elements/Space";
import { max, sortBy } from "lodash";

export class NabcSnippetVisualizer extends ChantLayoutElement {
  private _color;

  private _nabcSnippet: NabcSnippet;
  private _visualizers: ChantLayoutElement[] = [];
  private _hoizontalGap = 0;

  private get fontFamily() {
    return "gre" + this._nabcSnippet.mode;
  }

  constructor(context: ChantContext, nabcSnippet: NabcSnippet) {
    super(context);
    this._nabcSnippet = nabcSnippet;
    this._color = context.nabcColor;
  }

  performLayout() {
    this._visualizers = [];
    let x = 0;

    for (const neume of this._nabcSnippet.neumes) {
      // handle base glyph
      const glyphDescriptor = neume.glyphs
        .map((glyph) => glyph.basicGlyph + (glyph.modifier || ""))
        .join("!");
      const pp = neume.prepunctis?.map((sp) => sp.letter).join("") || "";
      const su = neume.subpunctis?.map((sp) => sp.letter).join("") || "";
      const sl = neume.significantLetters?.map((sp) => sp.letter).join("") || "";

      let ppManualDraw = false;
      let suManualDraw = false;
      let slManualDraw = false;

      let glyph = new Glyph(
        glyphDescriptor + pp + su + sl,
        this.fontFamily,
        this._context.nabcGlyphSize
      );

      if (glyph.code === -1) {
        glyph = new Glyph(glyphDescriptor + pp + su, this.fontFamily, this._context.nabcGlyphSize);
        ppManualDraw = false;
        suManualDraw = false;
        slManualDraw = true;
      }

      if (glyph.code === -1) {
        glyph = new Glyph(glyphDescriptor + pp, this.fontFamily, this._context.nabcGlyphSize);
        ppManualDraw = false;
        suManualDraw = true;
        slManualDraw = true;
      }

      if (glyph.code === -1) {
        glyph = new Glyph(glyphDescriptor + su, this.fontFamily, this._context.nabcGlyphSize);
        ppManualDraw = true;
        suManualDraw = false;
        slManualDraw = true;
      }

      if (glyph.code === -1) {
        glyph = new Glyph(glyphDescriptor + sl, this.fontFamily, this._context.nabcGlyphSize);
        ppManualDraw = true;
        suManualDraw = true;
        slManualDraw = false;
      }

      if (glyph.code === -1) {
        glyph = new Glyph(glyphDescriptor, this.fontFamily, this._context.nabcGlyphSize);
        ppManualDraw = true;
        suManualDraw = true;
        slManualDraw = true;
      }

      const baseGlyphVisualizer = new GlyphVisualizer(
        this._context,
        glyph,
        this._color,
        undefined,
        undefined,
        undefined,
        "none"
      );

      // handling pitch
      const pitch = neume.pitch.toLowerCase().charCodeAt(0) - "f".charCodeAt(0);
      baseGlyphVisualizer.bounds.setY(calculateNabcPositionFromPitch(this._context, pitch));

      this._visualizers.push(baseGlyphVisualizer);

      x = this.handleHorizontalAdjustment(neume, x);

      baseGlyphVisualizer.bounds.setX(x);

      if (ppManualDraw && neume.prepunctis) {
        x += this.handlePrepunctis(neume, baseGlyphVisualizer, x);
      }

      if (suManualDraw && neume.subpunctis) {
        x += this.handleSubpunctis(neume, baseGlyphVisualizer, x);
      }

      if (slManualDraw && neume.significantLetters) {
        x = this.handleSignificantLetters(neume, baseGlyphVisualizer, x);
      }

      // if (neume?.errorsCount) {
      //   x = this.handleErrors(neume, baseGlyphVisualizer, x);
      // }

      x += baseGlyphVisualizer.bounds.width;

      const index = this._nabcSnippet.neumes.indexOf(neume);
      if (index < this._nabcSnippet.neumes.length - 1) {
        const space = Space.createFromWidth(this._context, this._context.nabcinterelementspace);
        space.bounds.setX(x);
        x += space.bounds.width;
        this._visualizers.push(space);
      }
    }

    this.bounds = this._visualizers.reduce((bounds, visualizer) => {
      return bounds.horizontalUnion(visualizer.bounds);
    }, Rect.createEmpty());
  }

  private handleHorizontalAdjustment(neume: NabcNeume, x: number) {
    if (!neume.horizontalSpacingAdjustment) {
      return x;
    }

    const slashes = neume.horizontalSpacingAdjustment
      .split("")
      .filter((char) => char === "/").length;
    const quotes = neume.horizontalSpacingAdjustment
      .split("")
      .filter((char) => char === "`").length;

    const largeForwardSpace = Math.floor(slashes / 2);
    const singleForwardSpace = slashes % 2;

    const largeBackSpace = Math.floor(quotes / 2);
    const singleBackSpace = quotes % 2;

    const largerSpace = this._distanceConverter.lengthToPx(this._context.nabclargerspace);
    const singleSpace = this._distanceConverter.lengthToPx(this._context.nabcinterelementspace);

    return (
      x +
      largeForwardSpace * largerSpace +
      singleForwardSpace * singleSpace -
      largeBackSpace * largerSpace -
      singleBackSpace * singleSpace
    );
  }

  // private handleErrors(neume: NabcNeume, baseGlyphVisualizer: GlyphVisualizer, x: number) {
  //   let resultX = x;
  //   for (let i = 0; i < neume.errorsCount!; i++) {
  //     const rect = new RectangleVisualizer(this._context,
  //       Rect.create(baseGlyphVisualizer.bounds.width + x, -5, 4, 10));
  //     this._visualizers.push(rect);
  //     resultX += rect.bounds.width + this._hoizontalGap;
  //   }
  //   return resultX;
  // }

  private handleSignificantLetters(
    neume: NabcNeume,
    baseGlyphVisualizer: GlyphVisualizer,
    x: number
  ) {
    let reusultX = x;
    const sideLss = sortBy(
      neume.significantLetters!.filter((sl) => sl.positionAdjustment.x !== 0),
      (n) => n.positionAdjustment.x
    );
    for (const ls of sideLss) {
      const significativeLettersVisualizer = this.handlePositionedGlyph(
        new Glyph(ls.glyph, this.fontFamily, this._context.nabcGlyphSize),
        ls.positionAdjustment,
        baseGlyphVisualizer,
        ls.positionAdjustment.x < 0 ? 0 : x
      );

      this._visualizers.push(significativeLettersVisualizer);

      reusultX += significativeLettersVisualizer.bounds.width + this._hoizontalGap;
    }

    // need to handle separately because of the stacking issue
    const topLs = neume.significantLetters!.filter(
      (sl) => sl.positionAdjustment.x === 0 && sl.positionAdjustment.y === -1
    );
    const topVisualizers = this.handleMiddleSignificantLetters(topLs, baseGlyphVisualizer);
    this._visualizers.push(...topVisualizers);

    const bottomLs = neume.significantLetters!.filter(
      (sl) => sl.positionAdjustment.x === 0 && sl.positionAdjustment.y === 1
    );
    const bottomVisualizers = this.handleMiddleSignificantLetters(bottomLs, baseGlyphVisualizer);
    this._visualizers.push(...bottomVisualizers);

    return reusultX;
  }

  private handleSubpunctis(neume: NabcNeume, baseGlyphVisualizer: GlyphVisualizer, x: number) {
    const sbVisualizers: ChantLayoutElement[] = [];
    const orderedSbs = neume.subpunctis;

    if (orderedSbs.length === 0) {
      return 0;
    }

    const sbStr = orderedSbs.map((sb) => sb.letter).join("");
    
    const glyph = new Glyph(sbStr, this.fontFamily, this._context.nabcGlyphSize);

    if (glyph.code > -1) {
      const sbVisualizer = this.handlePositionedGlyph(
        glyph,
        orderedSbs[0].positionAdjustment,
        baseGlyphVisualizer,
        x
      );
      sbVisualizers.push(sbVisualizer);
    } else {
      for (let i = 0; i < orderedSbs.length; i++) {
        const sb = orderedSbs[i];
        const sbVisualizer = this.handlePositionedGlyph(
          new Glyph(sb.letter, this.fontFamily, this._context.nabcGlyphSize),
          sb.positionAdjustment,
          baseGlyphVisualizer,
          x
        );

        if (i > 0) {
          const prevSbVisualizer = sbVisualizers[i - 1];
          sbVisualizer.bounds.setY(prevSbVisualizer.bounds.bottom + 2);
        }

        sbVisualizers.push(sbVisualizer);
      }
    }

    this._visualizers.push(...sbVisualizers);
    const sbMaxWidth = max(sbVisualizers.map((v) => v.bounds.width)) || 0;
    return sbMaxWidth + this._hoizontalGap;
  }

  private handlePrepunctis(neume: NabcNeume, baseGlyphVisualizer: GlyphVisualizer, x: number) {
    // drawing in vise versa order
    const ppVisualizers: ChantLayoutElement[] = [];
    const orderedPps = neume.prepunctis.reverse();

    if (orderedPps.length === 0) {
      return 0;
    }

    const ppStr = orderedPps.map((pp) => pp.letter).join("");
    const glyph = new Glyph(ppStr, this.fontFamily, this._context.nabcGlyphSize);

    if (glyph.code > -1) {
      const ppVisualizer = this.handlePositionedGlyph(
        glyph,
        orderedPps[0].positionAdjustment,
        baseGlyphVisualizer,
        x
      );
      ppVisualizers.push(ppVisualizer);
    } else {
      for (let i = 0; i < orderedPps.length; i++) {
        const pp = orderedPps[i];
        const ppVisualizer = this.handlePositionedGlyph(
          new Glyph(pp.letter, this.fontFamily, this._context.nabcGlyphSize),
          pp.positionAdjustment,
          baseGlyphVisualizer,
          x
        );

        ppVisualizers.push(ppVisualizer);

        if (i > 0) {
          const prevPpVisualizer = ppVisualizers[i - 1];
          prevPpVisualizer.bounds.setY(ppVisualizer.bounds.bottom + 2);
        }
      }
    }

    this._visualizers.push(...ppVisualizers);
    const ppMaxWidth = max(ppVisualizers.map((v) => v.bounds.width)) || 0;
    return ppMaxWidth + this._hoizontalGap;
  }

  private handleMiddleSignificantLetters(
    middleLs: NabcSignificantLetter[],
    baseGlyphVisualizer: GlyphVisualizer
  ): ChantLayoutElement[] {
    const visualizers: ChantLayoutElement[] = [];

    for (let i = 0; i < middleLs.length; i++) {
      const ls = middleLs[i];

      const lsVisualizer = this.handlePositionedGlyph(
        new Glyph(ls.glyph, this.fontFamily, this._context.nabcGlyphSize),
        ls.positionAdjustment,
        baseGlyphVisualizer,
        0
      );

      if (i > 0) {
        const offset = lsVisualizer.bounds.width / 2;

        let prevLsVisualizer: ChantLayoutElement | undefined = undefined;
        for (let j = 0; j < visualizers.length; j++) {
          prevLsVisualizer = visualizers[j];
          prevLsVisualizer.bounds.moveX(-offset);
        }
        lsVisualizer.bounds.setX(prevLsVisualizer!.bounds.right);
      }

      visualizers.push(lsVisualizer);
    }

    return visualizers;
  }

  private handlePositionedGlyph(
    glyph: Glyph,
    positionAdjustment: Point,
    baseGlyphVisualizer: GlyphVisualizer,
    x: number
  ): GlyphVisualizer {
    const positionedVisualizer = new GlyphVisualizer(
      this._context,
      glyph,
      this._color,
      undefined,
      undefined,
      undefined,
      "none"
    );

    if (positionAdjustment.x > 0) {
      const right = Math.max(
        baseGlyphVisualizer.bounds.right,
        x + baseGlyphVisualizer.bounds.width
      );
      positionedVisualizer.bounds.setX(right + this._hoizontalGap);
    } else if (positionAdjustment.x === 0) {
      positionedVisualizer.bounds.setX(
        baseGlyphVisualizer.bounds.x + baseGlyphVisualizer.bounds.width / 2
      );
      positionedVisualizer.setTextAnchor("middle");
    } else {
      positionedVisualizer.bounds.setX(x);
      this._visualizers.forEach((v) => {
        v.bounds.moveX(positionedVisualizer.bounds.width + this._hoizontalGap);
      });
      // baseGlyphVisualizer.bounds.moveX(positionedVisualizer.bounds.width + this._hoizontalGap);
    }

    if (positionAdjustment.y > 0) {
      if (positionAdjustment.x === 0) {
        positionedVisualizer.bounds.setY(
          baseGlyphVisualizer.bounds.y + baseGlyphVisualizer.bounds.height
        );
      } else {
        positionedVisualizer.bounds.setY(
          baseGlyphVisualizer.bounds.y + baseGlyphVisualizer.bounds.height - positionedVisualizer.capHeight
        );
      }
    } else if (positionAdjustment.y < 0) {
      if (positionAdjustment.x === 0) {
        positionedVisualizer.bounds.setY(
          baseGlyphVisualizer.bounds.y -
            baseGlyphVisualizer.bounds.height -
            positionedVisualizer.bounds.height
        );
      } else {
        positionedVisualizer.bounds.setY(
          baseGlyphVisualizer.bounds.y +
          baseGlyphVisualizer.bounds.height - baseGlyphVisualizer.capHeight - positionedVisualizer.capHeight
        );
      }
    } else {
      positionedVisualizer.bounds.setY(
        baseGlyphVisualizer.bounds.y + baseGlyphVisualizer.bounds.height - baseGlyphVisualizer.capHeight
      );
    }

    return positionedVisualizer;
  }

  // protected drawDebugBounds() {
  //   return d3.create('svg:rect')
  //     .attr('x', this.bounds.x)
  //     .attr('y', this.bounds.y - this.bounds.height / 2)
  //     .attr('width', this.bounds.width)
  //     .attr('height', this.bounds.height)
  //     .attr('fill', 'none')
  //     .attr('stroke', 'red')
  //     .attr('stroke-width', 1)
  //     .node() as SVGRectElement;
  // }

  public createSvgFragments(): SVGElement[] {
    const nueme = document.createElementNS("http://www.w3.org/2000/svg", "g");
    nueme.setAttribute("class", "nabc-nueme");
    nueme.setAttribute("transform", `translate(${this.bounds.x}, ${this.bounds.y})`);

    for (const visualizer of this._visualizers) {
      const fragments = visualizer.createSvgFragments();
      for (const fragment of fragments) {
        nueme.appendChild(fragment);
      }
    }

    return [nueme, ...super.createSvgFragments()];
  }
}
